import Microgame from '../engine/Microgame.js';

export default class OrbitGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Orbite !`, en: `Orbit!`, es: `¡Órbita!` },
            controls: { fr: `Appuie n'importe où pour lancer le satellite (ou clic)`, en: `Tap anywhere to launch the satellite`, es: `Toca en cualquier lugar para lanzar el satélite` },
            objective: { fr: `Lâche le satellite pour qu'il touche la cible externe`, en: `Release the satellite so it hits the outer target`, es: `Lanza el satélite para que toque el objetivo externo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M12 2a10 10 0 1 1-10 10"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="5" r="2"/></svg>`,
            actions: [
                { type: 'wait', duration: 820 },
                { type: 'tap', x: 50, y: 50, duration: 180 },
                { type: 'wait', duration: 620 },
            ],
            loopDuration: 2600
        };
    }
    constructor(engine) {
        super(engine);
        this.planet = null;
        this.satellite = null;
        this.target = null;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.3;
        this.duration = 2400 * (speedMult / adj);
        this.effectiveSpeedMult = adj;
        
        this.angle = this.isPlayback ? 0 : (Math.random() * Math.PI * 2);
        this.speed = 5.5; // Base speed: 5.5 * 2.4s = 13.2 rad (~2.1 loops)
        this.radius = 100; // px
        
        // Ensure the target can be anywhere and still hit twice.
        // Total distance is 13.2 rad. 2 loops is 12.56 rad.
        // targetOffset in [0.5, 2PI] ensures 1st hit at startAngle + offset, 2nd at startAngle + offset + 2PI.
        // max possible distance needed = offset + 2PI. If offset = 2PI, we need 4PI = 12.56. 13.2 > 12.56.
        const targetOffset = this.isPlayback ? 4.6 : (0.5 + Math.random() * (Math.PI * 2 - 0.8));
        this.targetAngle = (this.angle + targetOffset) % (Math.PI * 2);

        this.stopped = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "ALIGN!";

        // Center Planet
        this.planet = document.createElement('div');
        this.planet.style.position = 'absolute';
        this.planet.style.width = '60px';
        this.planet.style.height = '60px';
        this.planet.style.background = this.primaryColor;
        this.planet.style.borderRadius = '50%';
        this.planet.style.left = '50%';
        this.planet.style.top = '50%';
        this.planet.style.transform = 'translate(-50%, -50%)';
        this.planet.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        this.container.appendChild(this.planet);

        // Orbit Path (Visual)
        const orbit = document.createElement('div');
        orbit.style.position = 'absolute';
        orbit.style.width = `${this.radius * 2}px`;
        orbit.style.height = `${this.radius * 2}px`;
        orbit.style.border = `2px dashed ${this.secondaryColor}`;
        orbit.style.opacity = '0.3';
        orbit.style.borderRadius = '50%';
        orbit.style.left = '50%';
        orbit.style.top = '50%';
        orbit.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(orbit);

        // Target Zone
        this.target = document.createElement('div');
        this.target.style.position = 'absolute';
        this.target.style.width = '30px';
        this.target.style.height = '30px';
        this.target.style.border = `3px solid ${this.secondaryColor}`;
        this.target.style.borderRadius = '50%';
        this.target.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.target.style.left = '50%';
        this.target.style.top = '50%';
        // Position target based on angle
        const tx = Math.cos(this.targetAngle) * this.radius;
        const ty = Math.sin(this.targetAngle) * this.radius;
        this.target.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`;
        this.container.appendChild(this.target);

        // Satellite
        this.satellite = document.createElement('div');
        this.satellite.style.position = 'absolute';
        this.satellite.style.width = '20px';
        this.satellite.style.height = '20px';
        this.satellite.style.background = this.secondaryColor;
        this.satellite.style.borderRadius = '50%';
        this.satellite.style.left = '50%';
        this.satellite.style.top = '50%';
        this.satellite.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
        const sx = Math.cos(this.angle) * this.radius;
        const sy = Math.sin(this.angle) * this.radius;
        this.satellite.style.transform = `translate(calc(-50% + ${sx}px), calc(-50% + ${sy}px))`;
        this.container.appendChild(this.satellite);

        // Input
        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);
    }

    onTap() {
        if (this.stopped || !this.active) return;
        this.stopped = true;
        this._frozenTimerValue = this.engine.timer;

        // Visual distance check (pixel perfect "touch")
        const sx = Math.cos(this.angle) * this.radius;
        const sy = Math.sin(this.angle) * this.radius;
        const tx = Math.cos(this.targetAngle) * this.radius;
        const ty = Math.sin(this.targetAngle) * this.radius;
        
        const dist = Math.hypot(sx - tx, sy - ty);
        // Satellite radius (10px) + Target radius (15px) + small margin = ~28px for "touch"
        if (dist <= 28) {
            this.target.style.background = this.secondaryColor;
            this.target.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
            setTimeout(() => this.win(), 400);
        } else {
            this.satellite.style.background = '#ff0000';
            setTimeout(() => this.lose(), 400);
        }
    }

    update(dt) {
        if (!this.active) return;

        if (this.stopped) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
            return;
        }

        this.angle += this.speed * (dt / 1000) * (this.effectiveSpeedMult || 1.0);

        const x = Math.cos(this.angle) * this.radius;
        const y = Math.sin(this.angle) * this.radius;

        this.satellite.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
